/**
 * ShadowAI.tsx — Shadow AI Monitor page
 *
 * Surfaces external AI platform usage detected on the company network.
 * All copy is in plain business language — no technical jargon.
 *
 * The user first picks a customer from the selector; data for that customer
 * is then loaded from the Shadow AI API.
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config/api';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import CreateTicketDialog from '../components/CreateTicketDialog';
import { useAuth } from '../context/AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Customer {
  id: string;
  name: string;
  organisation: string;
}

interface Summary {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  platforms_detected: number;
}

interface AiEvent {
  id: string;
  timestamp: string;
  employee_ip: string;
  platform_key: string;
  platform_name: string;
  vendor: string;
  domain_accessed: string;
  risk_level: string;
  data_risk: string;
  category: string;
  is_acknowledged: boolean;
}

interface DetectedPlatform {
  platform_key: string;
  platform_name: string;
  vendor: string;
  risk_level: string;
  data_risk: string;
  category: string;
  event_count: number;
  last_seen: string | null;
}

interface Agent {
  agent_id: string;
  hostname: string;
  os_type: string;
  agent_version: string;
  last_seen: string | null;
  status: 'active' | 'offline';
  registered_at: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

type ChipColor = 'error' | 'warning' | 'info' | 'success' | 'default';

const RISK_COLOR: Record<string, ChipColor> = {
  critical: 'error',
  high: 'error',
  medium: 'warning',
  low: 'info',
};

function RiskChip({ level }: { level: string }) {
  return (
    <Chip
      label={level.toUpperCase()}
      color={RISK_COLOR[level] ?? 'default'}
      size="small"
      sx={{ fontWeight: 700 }}
    />
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function categoryLabel(cat: string): string {
  const map: Record<string, string> = {
    generative_ai: 'Generative AI',
    code_ai: 'AI Code Assistant',
    search_ai: 'AI Search',
    ml_platform: 'ML Platform',
  };
  return map[cat] ?? cat;
}

// ── KPI card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value, accent, onClick }: { label: string; value: number | string; accent?: string; onClick?: () => void }) {
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        border: 1, borderColor: 'divider', height: '100%',
        ...(onClick && {
          cursor: 'pointer',
          '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', transition: 'all 0.2s' },
        }),
      }}
    >
      <CardContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
        <Typography variant="h4" fontWeight={700} color={accent ?? 'text.primary'}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

// ── Risk breakdown bar ────────────────────────────────────────────────────────

function RiskBar({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <Box mb={1.5}>
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" fontWeight={600}>{count}</Typography>
      </Box>
      <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.800', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 4, transition: 'width 0.6s ease' }} />
      </Box>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const OS_TABS = ['all', 'windows', 'macos', 'linux', 'ubuntu'] as const;
type OsTab = typeof OS_TABS[number];

const OS_LABEL: Record<OsTab, string> = {
  all: 'All OS',
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
  ubuntu: 'Ubuntu',
};

const OS_SCRIPT: Record<string, string> = {
  windows: 'windows',
  macos: 'macos',
  linux: 'linux',
  ubuntu: 'linux',
};

export default function ShadowAI() {
  const { user, loading: authLoading } = useAuth();
  const isCustomer = !authLoading && user?.role === 'customer';

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [events, setEvents] = useState<AiEvent[]>([]);
  const [platforms, setPlatforms] = useState<DetectedPlatform[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [riskFilter,   setRiskFilter]   = useState<string>('all');
  const [osFilter,     setOsFilter]     = useState<OsTab>('all');
  const [hours,        setHours]        = useState<number>(24);
  const [ticketEvent,  setTicketEvent]  = useState<AiEvent | null>(null);
  const [agentKey,     setAgentKey]     = useState<string | null>(null);
  const [keyLoading,   setKeyLoading]   = useState(false);
  const [keyCopied,    setKeyCopied]    = useState(false);

  // For customers: pin to their own org — never fetch the partner list.
  useEffect(() => {
    if (authLoading) return;
    if (isCustomer) {
      setSelectedCustomer(user?.customer_id ?? '');
      return;
    }
    axios.get<Customer[]>(`${API_BASE}/v1/customers/`)
      .then((r) => {
        setCustomers(r.data);
        if (r.data.length > 0) setSelectedCustomer(r.data[0].id);
      })
      .catch(() => setError('Could not load customers'));
  }, [authLoading, isCustomer, user?.customer_id]);

  const load = useCallback(async () => {
    if (!selectedCustomer) return;
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, eventsRes, platformsRes, agentsRes] = await Promise.all([
        axios.get<Summary>(`${API_BASE}/v1/shadow-ai/${selectedCustomer}/summary?hours=${hours}`),
        axios.get<AiEvent[]>(`${API_BASE}/v1/shadow-ai/${selectedCustomer}/events?hours=${hours}`),
        axios.get<DetectedPlatform[]>(`${API_BASE}/v1/shadow-ai/${selectedCustomer}/platforms?hours=${hours}`),
        axios.get<Agent[]>(`${API_BASE}/v1/shadow-ai/agent/${selectedCustomer}/agents`),
      ]);
      setSummary(summaryRes.data);
      setEvents(eventsRes.data);
      setPlatforms(platformsRes.data);
      setAgents(agentsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.message ?? 'Failed to load Shadow AI data');
    } finally {
      setLoading(false);
    }
  }, [selectedCustomer, hours]);

  useEffect(() => { load(); }, [load]);

  const loadDemo = async () => {
    if (!selectedCustomer) return;
    setDemoLoading(true);
    try {
      await axios.post(`${API_BASE}/v1/shadow-ai/${selectedCustomer}/demo`);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setDemoLoading(false);
    }
  };

  const acknowledge = async (eventId: string) => {
    if (!selectedCustomer) return;
    try {
      await axios.post(`${API_BASE}/v1/shadow-ai/${selectedCustomer}/acknowledge/${eventId}`);
      setEvents((prev) => prev.map((e) => e.id === eventId ? { ...e, is_acknowledged: true } : e));
    } catch {
      // Non-critical — silently ignore
    }
  };

  const handleInstallAgent = (os: string) => {
    const scriptKey = OS_SCRIPT[os] ?? os;
    const keyParam  = agentKey ? `?key=${encodeURIComponent(agentKey)}` : '';
    window.open(`${API_BASE}/v1/shadow-ai/agent/install/${scriptKey}${keyParam}`, '_blank');
  };

  const generateAgentKey = async () => {
    if (!selectedCustomer) return;
    setKeyLoading(true);
    setAgentKey(null);
    setKeyCopied(false);
    try {
      const res = await axios.post(`${API_BASE}/v1/shadow-ai/${selectedCustomer}/agent-key`);
      setAgentKey(res.data.agent_key);
    } catch (err: any) {
      setError(err.response?.data?.detail ?? 'Failed to generate agent key');
    } finally {
      setKeyLoading(false);
    }
  };

  const copyKey = () => {
    if (!agentKey) return;
    navigator.clipboard.writeText(agentKey).then(() => {
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 3000);
    });
  };

  const filteredEvents = events.filter((e) => {
    if (riskFilter !== 'all' && e.risk_level !== riskFilter) return false;
    return true;
  });

  const filteredAgents = osFilter === 'all'
    ? agents
    : agents.filter((a) => a.os_type.toLowerCase() === osFilter);

  return (
    <Box>
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4">Shadow AI Monitor</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Detect AI tools used by employees and flag data leaving the organisation
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button size="small" variant="outlined" color="secondary" onClick={loadDemo} disabled={demoLoading || !selectedCustomer}>
            {demoLoading ? <CircularProgress size={14} /> : 'Load Demo Data'}
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Controls row ──────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        {/* Partner selector — removed from DOM for customer role */}
        {!isCustomer && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Partner</InputLabel>
            <Select
              value={selectedCustomer}
              label="Partner"
              onChange={(e) => setSelectedCustomer(e.target.value)}
            >
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Time window</InputLabel>
          <Select value={hours} label="Time window" onChange={(e) => setHours(Number(e.target.value))}>
            <MenuItem value={6}>Last 6 hours</MenuItem>
            <MenuItem value={24}>Last 24 hours</MenuItem>
            <MenuItem value={48}>Last 48 hours</MenuItem>
            <MenuItem value={168}>Last 7 days</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* ── KPI cards ─────────────────────────────────────────────────────── */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Total AI Events" value={loading ? '—' : summary?.total ?? 0} onClick={() => setRiskFilter('all')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Critical Risk Events" value={loading ? '—' : summary?.critical ?? 0} accent="#ef233c" onClick={() => setRiskFilter('critical')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="High Risk Events" value={loading ? '—' : summary?.high ?? 0} accent="#f77f00" onClick={() => setRiskFilter('high')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Platforms Detected" value={loading ? '—' : summary?.platforms_detected ?? 0} accent="#00b4d8" onClick={() => setRiskFilter('all')} />
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={4}>
        {/* ── Platform table ────────────────────────────────────────────────── */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>Platforms Being Used</Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : platforms.length === 0 ? (
            <Alert severity="info">
              No AI platforms detected. Click <strong>Load Demo Data</strong> to see an example.
            </Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'background.default' } }}>
                    <TableCell>Platform</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Risk</TableCell>
                    <TableCell align="right">Events</TableCell>
                    <TableCell>Last Seen</TableCell>
                    <TableCell>What's at risk</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {platforms.map((p) => (
                    <TableRow key={p.platform_key} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body2" fontWeight={600}>{p.platform_name}</Typography>
                          {p.platform_key === 'deepseek' && (
                            <Tooltip title="Data sent to servers in China — potential GDPR and data sovereignty violation">
                              <Chip
                                icon={<WarningAmberIcon />}
                                label="Chinese servers"
                                color="error"
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">{categoryLabel(p.category)}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2">{p.vendor}</Typography></TableCell>
                      <TableCell><RiskChip level={p.risk_level} /></TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>{p.event_count}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {p.last_seen ? formatTime(p.last_seen) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="caption" color="text.secondary">{p.data_risk}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>

        {/* ── Risk breakdown ────────────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom>Risk Breakdown</Typography>
          <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', p: 3 }}>
            {summary && summary.total > 0 ? (
              <>
                <RiskBar label="Critical" count={summary.critical} total={summary.total} color="#ef233c" />
                <RiskBar label="High"     count={summary.high}     total={summary.total} color="#f77f00" />
                <RiskBar label="Medium"   count={summary.medium}   total={summary.total} color="#f4a11d" />
                <RiskBar label="Low"      count={summary.low}      total={summary.total} color="#2dc653" />
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  {summary.total} event{summary.total !== 1 ? 's' : ''} in the last {hours}h
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">No data for this window.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Agent Status ───────────────────────────────────────────────────── */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h6">Agent Status</Typography>
            <Typography variant="body2" color="text.secondary">
              Machines with the Shield network agent installed
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={keyLoading ? <CircularProgress size={14} color="inherit" /> : <KeyIcon />}
              onClick={generateAgentKey}
              disabled={keyLoading || !selectedCustomer}
            >
              {agentKey ? 'Rotate Agent Key' : 'Generate Agent Key'}
            </Button>
            {(['windows', 'macos', 'linux'] as const).map((os) => (
              <Button
                key={os}
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleInstallAgent(os)}
              >
                Install on {OS_LABEL[os]}
              </Button>
            ))}
          </Stack>
        </Box>

        {/* One-time agent key display */}
        {agentKey && (
          <Alert
            severity="warning"
            sx={{ mb: 2, fontFamily: 'monospace' }}
            action={
              <Button
                size="small"
                color="inherit"
                startIcon={<ContentCopyIcon />}
                onClick={copyKey}
              >
                {keyCopied ? 'Copied!' : 'Copy'}
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={700} gutterBottom>
              Agent key generated — copy it now. It will not be shown again.
            </Typography>
            <Typography
              component="code"
              sx={{
                display: 'block',
                wordBreak: 'break-all',
                bgcolor: 'rgba(0,0,0,0.15)',
                p: 1,
                borderRadius: 1,
                fontSize: '0.8rem',
                letterSpacing: 0.5,
              }}
            >
              {agentKey}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Use this key as <strong>SHIELD_API_KEY</strong> when running the install script.
              The Install buttons above will pre-fill it in the downloaded script.
            </Typography>
          </Alert>
        )}

        <Tabs
          value={osFilter}
          onChange={(_, v) => setOsFilter(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {OS_TABS.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  {OS_LABEL[tab]}
                  {tab !== 'all' && (
                    <Chip
                      label={agents.filter((a) => a.os_type.toLowerCase() === tab).length}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
        ) : filteredAgents.length === 0 ? (
          <Alert severity="info">
            No agents registered{osFilter !== 'all' ? ` for ${OS_LABEL[osFilter]}` : ''}. Install the agent on employee machines to start monitoring.
          </Alert>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'background.default' } }}>
                  <TableCell>Status</TableCell>
                  <TableCell>Hostname</TableCell>
                  <TableCell>OS</TableCell>
                  <TableCell>Agent Version</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell>Registered</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAgents.map((a) => (
                  <TableRow key={a.agent_id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CircleIcon
                          sx={{
                            fontSize: 10,
                            color: a.status === 'active' ? 'success.main' : 'text.disabled',
                          }}
                        />
                        <Typography variant="caption" color={a.status === 'active' ? 'success.main' : 'text.disabled'}>
                          {a.status === 'active' ? 'Active' : 'Offline'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{a.hostname || a.agent_id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={a.os_type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">v{a.agent_version}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {a.last_seen ? formatTime(a.last_seen) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(a.registered_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── Events timeline ────────────────────────────────────────────────── */}
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="h6">Access Timeline</Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Filter by risk</InputLabel>
          <Select value={riskFilter} label="Filter by risk" onChange={(e) => setRiskFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="critical">Critical only</MenuItem>
            <MenuItem value="high">High only</MenuItem>
            <MenuItem value="medium">Medium only</MenuItem>
            <MenuItem value="low">Low only</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : filteredEvents.length === 0 ? (
        <Alert severity="info">No events match the current filter.</Alert>
      ) : (
        <Stack spacing={1}>
          {filteredEvents.map((e) => (
            <Paper
              key={e.id}
              elevation={0}
              sx={{
                border: 1,
                borderColor: e.risk_level === 'critical' ? 'error.dark'
                  : e.risk_level === 'high' ? 'warning.dark'
                  : 'divider',
                p: 2,
                opacity: e.is_acknowledged ? 0.55 : 1,
                transition: 'opacity 0.3s',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                    <RiskChip level={e.risk_level} />
                    <Typography variant="body2" fontWeight={600}>
                      Employee at {e.employee_ip} accessed {e.platform_name}
                    </Typography>
                    {e.platform_key === 'deepseek' && (
                      <Chip
                        icon={<WarningAmberIcon />}
                        label="Chinese servers"
                        color="error"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">{e.data_risk}</Typography>
                  <Typography variant="caption" color="text.disabled">
                    via {e.domain_accessed} · {formatTime(e.timestamp)}
                  </Typography>
                </Box>

                <Stack spacing={0.75} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                  {(e.risk_level === 'critical' || e.risk_level === 'high') && (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      startIcon={<ConfirmationNumberIcon />}
                      onClick={() => setTicketEvent(e)}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Request Shield Review
                    </Button>
                  )}
                  {!e.is_acknowledged ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => acknowledge(e.id)}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Mark reviewed
                    </Button>
                  ) : (
                    <Chip label="Reviewed" size="small" color="success" variant="outlined" />
                  )}
                </Stack>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      {/* ── Ticket creation dialog ─────────────────────────────────────── */}
      <CreateTicketDialog
        open={!!ticketEvent}
        onClose={() => setTicketEvent(null)}
        customerId={selectedCustomer}
        shadowAiEventId={ticketEvent?.id}
        previewTitle={
          ticketEvent
            ? `Employees using ${ticketEvent.platform_name} — potential data exposure`
            : ''
        }
        previewPriority={
          ticketEvent?.risk_level === 'critical' ? 'critical'
          : ticketEvent?.risk_level === 'high' ? 'high'
          : 'medium'
        }
        previewProvider="shadow_ai"
        previewResource={ticketEvent?.platform_name ?? ''}
        previewScore={
          ticketEvent?.risk_level === 'critical' ? 95
          : ticketEvent?.risk_level === 'high' ? 75
          : 50
        }
      />
    </Box>
  );
}
